import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import { TFunction } from 'i18next';

export interface ImportCountTemple {
  id: string;
  index?: string;         //序号
  rowIndex?: number;      //Excel中实际行号（从1开始，表头为第1行）
  productCode: string;    //存货编码
  productName?: string;   //存货名称
  productModal?: string;  //规格型号
  productUom?: string;    //单位
  bookNum?: string;       //帐存数量
  countedNum: string | undefined;     //初盘数量
  locationIdx: string;    //库位
  label1?: string;    //库位标签
}

export type ValiedRaeseResult = ImportCountTemple & { countedNum: string };

export interface ParseResult {
  validData: Array<ValiedRaeseResult>;      // 有效数据
  filteredData: ImportCountTemple[];  // 被过滤的数据
}

// 表头定义 - 返回函数以支持国际化
export const getHeaders = (t: TFunction): string[] => [
  t('order.serialMumber'),
  t('counting.productCode'),
  t('counting.productName'),
  t('counting.productModal'),
  t('counting.productUom'),
  t('counting.bookNum'),
  t('counting.countedNum'),
  t('counting.locationIdx'),
  t('warehouse.label1'),
];

/**
 * 解析上传的Excel文件
 */
export const parseExcelFile = (file: File, t: TFunction): Promise<ParseResult> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];

        // 解析为二维数组
        const jsonData = XLSX.utils.sheet_to_json<string[]>(worksheet, { header: 1 });

        if (!jsonData || jsonData.length < 2) {
          reject(new Error('invalid'));
          return;
        }

        // 验证表头
        const headers = jsonData[0];
        const currentHeaders = getHeaders(t);
        const isValidHeader = currentHeaders.length === headers.length;
        if (!isValidHeader) {
          reject(new Error('invalid'));
          return;
        }

        // 解析数据行（跳过表头）
        const validData: Array<ImportCountTemple & { countedNum: string }> = [];
        const filteredData: ImportCountTemple[] = [];

        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i];
          if (!row || row.length === 0) continue;

          // 构建数据对象，包含Excel实际行号（表头为第1行，数据从第2行开始）
          const rowData: ImportCountTemple = {
            id: i + row[7] + '',
            index: row[0] ? String(row[0]).trim() : undefined,
            rowIndex: i + 1,  // Excel中实际行号（jsonData索引+1）
            productCode: row[1] ? String(row[1]).trim() : '',
            productName: row[2] ? String(row[2]).trim() : undefined,
            productModal: row[3] ? String(row[3]).trim() : undefined,
            productUom: row[4] ? String(row[4]).trim() : undefined,
            bookNum: row[5] ? String(row[5]).trim() : undefined,
            countedNum: row[6] !== undefined && row[6] !== null ? String(row[6]).trim() : undefined,
            locationIdx: row[7] ? String(row[7]).trim() : '',
            label1: row[8] ? String(row[8]).trim() : undefined,
          };
          // 过滤空行：存货编码(row[1])、初盘数量(row[6])、库位(row[7])都有数据才算有效
          const hasContent =
            row[1] !== undefined && row[1] !== null && String(row[1]).trim() !== '' &&
            row[6] !== undefined && row[6] !== null &&
            row[7] !== undefined && row[7] !== null && String(row[7]).trim() !== '';

          if (hasContent) {
            validData.push(rowData as ValiedRaeseResult);
          } else {
            filteredData.push(rowData);
          }
        }

        resolve({
          validData,
          filteredData
        });
      } catch {
        reject(new Error('invalid'));
      }
    };
    reader.onerror = () => reject(new Error('invalid'));
    reader.readAsArrayBuffer(file);
  });
};

/**
 * 生成盘点导入模板并下载
 */
export const generateTemple = (t: TFunction) => {
  // 工作表数据（仅表头）
  const headers = getHeaders(t);
  const worksheetData = [headers];

  // 创建工作表
  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

  // 设置列宽
  worksheet['!cols'] = [
    { wch: 8 },   // 序号
    { wch: 15 },  // 存货编码
    { wch: 20 },  // 存货名称
    { wch: 15 },  // 规格型号
    { wch: 8 },   // 单位
    { wch: 12 },  // 帐存数量
    { wch: 12 },  // 初盘数量
    { wch: 15 },  // 库位
    { wch: 22 },  // 库位标签
  ];

  // 创建工作簿
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, t('counting.inventoryImportTemplate'));

  // 生成Excel文件并下载
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const dataBlob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
  });

  // 生成文件名（带时间戳）
  const timestamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  saveAs(dataBlob, `${t('counting.inventoryImportTemplate')}_${timestamp}.xlsx`);
}